<template>
  <panel class="financial-plan">
    <template slot="toolbar">
      <w-select class="mr-3 flex-grow-1" :model="$schema.planType" :values="FINANCIAL_PLAN_TYPES" />

      <w-select class="mr-3 flex-grow-1" :model="$schema.startDate" :values="availableStartMonths" />

      <w-select class="mr-3 flex-grow-1" :model="$schema.endDate" :values="availableEndMonths" />

      <yes-or-no-radio class="mr-3 flex-grow-1" :model="$schema.isUkraine" />

      <w-button class="btn-primary mt-4" text="Filtruj" icon="fas fa-search" @click="applyFilters" />
    </template>

    <table
      v-if="planRecords && planRecords.length"
      class="table table-bordered table-hover table-striped w-100 dataTable dtr-inline"
    >
      <thead>
        <tr>
          <th v-for="(column, index) of columns" :key="'header_' + index" :style="{ 'max-width': column.maxWidth }">
            {{ column.displayName }}
          </th>
        </tr>
      </thead>

      <tbody>
        <tr v-for="(record, index) of planRecords" :key="'row_' + index">
          <td>{{ record.name }}</td>

          <td class="text-right">{{ formatAmount(record.amount) }}</td>

          <td><input v-model="record.comment" class="form-control" type="text" /></td>
        </tr>
      </tbody>

      <tfoot v-if="displayTotalRow">
        <tr>
          <th>Suma</th>

          <td class="text-right">{{ formatAmount(total) }}</td>

          <td />
        </tr>
      </tfoot>
    </table>

    <p v-else>Brak danych do wygenerowania planu.</p>

    <div slot="footer">
      <post-form text="Podgląd PDF" icon="fas fa-download mr-2" class="mr-3" :url="pdfUrl" :fields="pdfFields" />

      <post-form text="Pobierz CSV" icon="fas fa-download mr-2" class="mr-3" :url="csvUrl" :fields="pdfFields" />

      <w-button text="Prześlij do MDOK" class="btn-primary" @click="sendToMdok" />
    </div>
  </panel>
</template>

<script>
import { mapState } from 'vuex';

import YesOrNoRadio from '@/components/forms/document/YesOrNoRadio.vue';
import Panel from '@/components/panel/Panel.vue';
import PostForm from '@/components/postForm/PostForm.vue';
import { Errors } from '@/enums/errors.enum';
import { FinancialPlanType, FINANCIAL_PLAN_TYPES, FINANCIAL_PLAN_TYPE_DEFAULT } from '@/enums/financialPlanType.enum';
import { PublicSettings } from '@/enums/publicSettings.enum';
import { Successes } from '@/enums/successes.enum';
import { formatAmount } from '@/filters/formatAmount';
import { formMixin } from '@/mixins/form';
import { notificationsMixin } from '@/mixins/notifications';
import { financialPlanFiltersSchema } from '@/schemas/financialPlanFilters.schema';
import { config } from '@/utils/config';
import { getFinancialPlan, getFinancialPlanFilters } from '@/utils/financialPlan';
import { generateSchoolYearMonthOptions } from '@/utils/schoolYear';
import { serverError } from '@/utils/serverError';

// @vuese
// Plan finansowy
// @group Reports
export default {
  components: { Panel, PostForm, YesOrNoRadio },
  mixins: [formMixin, notificationsMixin],

  schema: financialPlanFiltersSchema,

  data() {
    const currentDate = new Date();
    const schoolYearStart = currentDate.getMonth() >= 8 ? currentDate.getFullYear() : currentDate.getFullYear() - 1;

    return {
      model: getFinancialPlanFilters(this.$route),

      FinancialPlanType,
      FINANCIAL_PLAN_TYPES,

      availableStartMonths: generateSchoolYearMonthOptions(schoolYearStart),
      availableEndMonths: generateSchoolYearMonthOptions(schoolYearStart, 'monthEnd')
    };
  },

  computed: {
    ...mapState({
      planRecords: state => state.financialPlan.records,

      columns(state) {
        const setting = state.setting.settings[PublicSettings.FINANCIAL_PLAN_COLUMNS];
        if (!setting) {
          return [];
        }
        const columns = JSON.parse(setting.value);
        return columns.map(column => {
          let displayName = typeof column.displayName === 'string' ? column.displayName : column.displayName[this.type];
          if (column.displayNameTransformer) {
            displayName = new Function('column', 'type', 'isUkraine', column.displayNameTransformer)(
              column,
              this.type,
              this.isUkraine
            );
          }
          return {
            ...column,
            displayName
          };
        });
      }
    }),

    type() {
      return this.$route.query.planType || FINANCIAL_PLAN_TYPE_DEFAULT;
    },

    isUkraine() {
      return this.$route.query.isUkraine || false;
    },

    csvUrl() {
      return (
        config.csvURL +
        '/financial-plan/' +
        this.$route.params.procedureId +
        '?type=' +
        this.type +
        '&isUkraine=' +
        this.$route.query.isUkraine
      );
    },

    pdfUrl() {
      return config.renderURL + '/pdf/financial-plan/' + this.$route.params.procedureId + '?type=' + this.type;
    },

    pdfFields() {
      const filters = getFinancialPlanFilters(this.$route);
      return [
        { name: 'records', value: this.planRecords },
        ...Object.keys(filters).map(key => ({ name: key, value: filters[key] }))
      ];
    },

    total() {
      return this.planRecords.reduce((total, record) => total + record.amount, 0);
    },

    displayTotalRow() {
      return ![FinancialPlanType.OW, FinancialPlanType.TOTAL].includes(
        this.$route.query.planType || FINANCIAL_PLAN_TYPE_DEFAULT
      );
    }
  },

  methods: {
    // @vuese
    // Nakłada wybrane filtry
    applyFilters() {
      if (this.$schema.$validateAll().length) {
        this.notifyError(Errors.FORM_CORRECT_ERRORS);
        return;
      }
      this.$router.replace({ query: this.$schema.$getData() });
    },

    formatAmount,

    // @vuese
    // Przesłanie planu do MDOK
    sendToMdok() {
      const data = {
        records: this.planRecords.map(({ name, amount, comment }) => ({ name, amount, comment })),
        ...getFinancialPlanFilters(this.$route)
      };
      return this.$store
        .dispatch('financialPlan/sendToMdok', data)
        .then(() => this.notifySuccess(Successes.FINANCIAL_PLAN_SENT))
        .catch(err => this.notifyError(serverError(err)));
    }
  },

  beforeRouteUpdate(to, from, next) {
    getFinancialPlan(to)
      .then(() => next())
      .catch(err => this.notifyError(serverError(err)));
  }
};
</script>

<style>
.financial-plan .panel-toolbar {
  width: 100%;
  align-items: start;
  padding: 15px;
}
</style>
